import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';
import type { PaginatedResponse } from '@/types/pagination.ts';
import type {
  InsightChatItem,
  InsightChatQuestion,
  InsightChatState,
} from '@wundo/shared/insightChat.ts';
import type {
  InsightsModel,
  InsightsReasoningEffort,
} from '@wundo/shared/insightsModel.ts';
import { PAGE_SIZE } from '@/lib/pageSize.ts';

export interface InsightRoutine {
  id: string;
  name: string;
  prompt: string;
  schedule_cron: string | null;
  interval_anchor: string | null;
  /** Slack channel id; null means the default channel. */
  slack_channel: string | null;
  /** The analyst's notes for its next run; null when there are none. */
  memory: string | null;
  /** null: the routine runs on the code default. */
  model: InsightsModel | null;
  reasoning_effort: InsightsReasoningEffort | null;
  enabled: boolean;
  created_by_id: string;
  created_by: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
  created_at: string;
  updated_at: string;
}

/** A routine as the overview lists it: with its most recent run. */
export interface InsightRoutineListItem extends InsightRoutine {
  /** Null when the routine never ran. */
  last_run: {
    id: string;
    status: InsightRunStatus;
    ran_at: string;
  } | null;
}

/** What the overview can sort routines on. */
export type RoutineSortKey = 'name' | 'owner' | 'last_run' | 'enabled';

export type InsightRunStatus =
  | 'pending'
  | 'running'
  | 'finished'
  | 'failed'
  | 'cancelled';

export interface InsightRun {
  id: string;
  routine_id: string | null;
  conversation_id: string | null;
  status: InsightRunStatus;
  report: string | null;
  teaser: string | null;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

/** A run as the history lists it. */
export interface InsightRunSummary extends InsightRun {
  /** Live while the conversation exists: the chat keeps adding to it. */
  cost_usd: number;
}

export interface InsightRunDetail extends InsightRunSummary {
  /** The ceiling for the analysis and the chat together. */
  budget_usd: number;
  /** Whether a question may be asked now, and if not, why. */
  chat_state: InsightChatState;
  routine_name: string | null;
  /** The model and effort the chat on this run answers with: the routine's current ones. */
  routine_model: InsightsModel;
  routine_reasoning_effort: InsightsReasoningEffort;
  /** Owner of the run's conversations, needed to open a trace in /messages. */
  user_id?: string | null;
  /** Subagent conversations spawned by this run, for the trace links. */
  subagent_conversation_ids?: string[];
}

export interface RoutineInput {
  name: string;
  prompt: string;
  schedule_cron: string | null;
  interval_anchor: string | null;
  slack_channel: string | null;
  model: InsightsModel | null;
  reasoning_effort: InsightsReasoningEffort | null;
  enabled: boolean;
  /** Only an edit carries notes: a new routine has none yet. */
  memory?: string | null;
}

export interface SlackDestination {
  id: string;
  /** Display label including its sigil: `#general`, `@bram`. */
  name: string;
  type: 'channel' | 'user';
}

/** How many routines one page of the overview holds. */
export const ROUTINE_PAGE_SIZE = 50;

/** The deployment's default channel, used to preselect a new routine. */
export async function fetchDefaultSlackDestination(): Promise<SlackDestination> {
  return await apiFetchJson<SlackDestination>('/slack-destinations/default');
}

/** One destination by id, so a stored channel can be shown by name. */
export async function fetchSlackDestination(id: string): Promise<SlackDestination> {
  return await apiFetchJson<SlackDestination>(
    `/slack-destinations/${encodeURIComponent(id)}`,
  );
}

/** Channels and people matching a query; an empty query lists the first page. */
export async function searchSlackDestinations(
  query: string,
  signal?: AbortSignal,
): Promise<SlackDestination[]> {
  const suffix = query.trim() ? `?query=${encodeURIComponent(query.trim())}` : '';
  const response = await apiFetchJson<PaginatedResponse<SlackDestination>>(
    `/slack-destinations${suffix}`,
    { signal },
  );

  return response.data;
}

export async function fetchRoutines(
  page = 1,
  sort: { key: RoutineSortKey; direction: 'asc' | 'desc' } = {
    key: 'last_run',
    direction: 'desc',
  },
  limit = ROUTINE_PAGE_SIZE,
): Promise<PaginatedResponse<InsightRoutineListItem>> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    sort_by: sort.key,
    sort_order: sort.direction,
  });

  return await apiFetchJson<PaginatedResponse<InsightRoutineListItem>>(
    `/insight-routines?${params.toString()}`,
  );
}

export async function fetchRoutine(id: string): Promise<InsightRoutine> {
  return await apiFetchJson<InsightRoutine>(`/insight-routines/${id}`);
}

export async function createRoutine(input: RoutineInput): Promise<InsightRoutine> {
  return await apiFetchJson<InsightRoutine>('/insight-routines', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateRoutine(
  id: string,
  input: Partial<RoutineInput>,
): Promise<InsightRoutine> {
  return await apiFetchJson<InsightRoutine>(`/insight-routines/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Answers 204 with no body. */
export async function deleteRoutine(id: string): Promise<void> {
  await apiFetch(`/insight-routines/${id}`, { method: 'DELETE' });
}

/** Queues one iteration now and hands back the run it created. */
export async function runRoutine(id: string): Promise<InsightRun> {
  return await apiFetchJson<InsightRun>(`/insight-routines/${id}/actions/run`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function fetchRuns(
  routineId: string,
  page = 1,
  limit = PAGE_SIZE,
): Promise<PaginatedResponse<InsightRunSummary>> {
  return await apiFetchJson<PaginatedResponse<InsightRunSummary>>(
    `/insight-runs?routine_id=${routineId}&page=${page}&limit=${limit}`,
  );
}

export async function fetchRun(id: string): Promise<InsightRunDetail> {
  return await apiFetchJson<InsightRunDetail>(`/insight-runs/${id}`);
}

/** Stops the analysis still going, or the answer being written. Answers 204 with no body. */
export async function stopRun(id: string): Promise<void> {
  await apiFetch(`/insight-runs/${id}/actions/stop`, { method: 'POST' });
}

/** The run's conversation as its chat, oldest first. */
export async function fetchRunChat(id: string): Promise<InsightChatItem[]> {
  return await apiFetchJson<InsightChatItem[]>(`/insight-runs/${id}/messages`);
}

/** Puts a question to the analyst as the signed-in staff member; the answer arrives on the socket. */
export async function askRun(id: string, text: string): Promise<InsightChatQuestion> {
  return await apiFetchJson<InsightChatQuestion>(`/insight-runs/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  });
}
